import { Component, OnInit, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { OwnerLeadProfileService } from '@app/_components/owner-lead-profile/owner-lead-profile.service';
import { ProformaService } from '@app/_components/proforma/proforma.service';
import { AppEventService } from '@app/_components/app-event/app-event.service';
import { AlertService, AuthenticationService, UserService } from '@app/_services';
import { TaskService } from '@app/_components/task/task.service';

interface FunnelStage {
    label: string;
    value: number;
    count: number;
    color: string;
}

interface PropertySubRow {
    propertyLeadProfileId: number;
    displayName: string;
    developmentName: string;
    bedroomNum: number | null;
    bathroomNum: number | null;
    subjValue: number | null;
    proformaId: number | null;
    proformaConfId: string | null;
    proformaSent: Date | null;
    viewCount: number;
    lastViewed: Date | null;
    hot: boolean;
}

interface PipelineRow {
    ownerLeadProfileId: number;
    ownerName: string;
    propertyName: string;
    leadStatus: number;
    leadStatusLabel: string;
    assignedAgent: string;
    assignedAgentId: number | null;
    proformaConfId: string | null;
    proformaId: number | null;
    proformaSent: Date | null;
    lastViewed: Date | null;
    viewCount: number;
    daysActive: number;
    daysSinceUpdate: number;
    isStale: boolean;
    leadScore: number;
    hot: boolean;
    createDate: Date | null;
    lastUpdate: Date | null;
    properties: PropertySubRow[];
    rawData: any;
}

interface ActivityItem {
    text: string;
    date: Date;
    icon: string;
    type: string;
}

@Component({
    selector: 'app-bizdev-pipeline',
    templateUrl: './bizdev-pipeline.component.html',
    styleUrls: ['./bizdev-pipeline.component.scss'],
    providers: [MessageService],
    standalone: false,
})
export class BizdevPipelineComponent implements OnInit {

    // Top metrics (from pipeline-metrics endpoint)
    totalActiveLeads = 0;
    proformasSentMonth = 0;
    leadsWithViewsThisWeek = 0;
    convertedThisMonth = 0;
    metricsWindowDays = 30;

    metricsLoading = false;

    // Funnel (from funnel-counts endpoint)
    funnelStages: FunnelStage[] = [
        { label: 'Prospect',    value: 1, count: 0, color: '#2C4A6E' },
        { label: 'Lead',        value: 2, count: 0, color: '#3F6694' },
        { label: 'Opportunity', value: 3, count: 0, color: '#C5A572' },
        { label: 'Win',         value: 4, count: 0, color: '#299A01' },
        { label: 'Loss',        value: 5, count: 0, color: '#9B2C2C' },
    ];
    funnelTotal = 0;
    selectedStage: number | null = null;

    // Agent filter toggle
    agentFilter: 'all' | 'me' | 'team' | 'specific' = 'all';
    currentUserId: number | null = null;
    selectedAgentId: number | null = null;
    selectedAgentName: string | null = null;

    // Table
    rows: PipelineRow[] = [];
    filteredRows: PipelineRow[] = [];
    tableLoading = false;

    // Search
    searchQuery = '';

    // Date range filter
    dateRangeFilter: 'all' | 'week' | 'month' | 'quarter' = 'all';

    // Expanded row keys for PrimeNG (keyed by ownerLeadProfileId)
    expandedRowKeys: { [key: string]: boolean } = {};

    // View stats keyed by proformaId string
    viewStatsMap: { [proformaId: string]: { proformaId: number; viewCount: number; lastViewedAt: string | null } } = {};

    // Activity feed
    activityFeed: ActivityItem[] = [];
    activityLoading = false;

    // Inline status change
    statusMenuOpenId: number | null = null;
    statusUpdating: { [id: string]: boolean } = {};

    // Inline agent assign
    agentMenuOpenId: number | null = null;
    agentUpdating: { [id: string]: boolean } = {};
    salesAgents: any[] = [];

    // Bulk selection
    selectedRows: PipelineRow[] = [];
    bulkStatusMenuOpen = false;
    bulkAgentMenuOpen = false;
    bulkUpdating = false;

    // Log activity panel (lives in expanded row)
    noteOpenId: number | null = null;
    noteText = '';
    noteType = 'NOTE';
    noteSaving = false;
    noteTypes = [
        { name: 'Note',    value: 'NOTE'    },
        { name: 'Call',    value: 'CALL'    },
        { name: 'Email',   value: 'EMAIL'   },
        { name: 'Meeting', value: 'MEETING' },
    ];

    // Follow-up scheduling
    followUpOpenId: number | null = null;
    followUpDate: string = '';
    followUpNote: string = '';
    followUpSaving = false;
    today = new Date().toISOString().split('T')[0];

    // Velocity panel
    velocityStages: any[] = [];
    velocityLoading = false;
    velocityPanelOpen = false;

    // Refresh state
    lastRefresh: Date | null = null;

    // Compose dialog
    composeVisible = false;
    composeRow: any = null;

    // My Team filter
    teamUserIds: number[] | null = null;

    // Message thread per lead (loaded lazily on row expand)
    leadMessages: { [leadId: number]: any[] } = {};
    leadMessagesLoading: { [leadId: number]: boolean } = {};

    constructor(
        private ownerLeadService: OwnerLeadProfileService,
        private proformaService: ProformaService,
        private appEventService: AppEventService,
        private authService: AuthenticationService,
        private userService: UserService,
        private alertService: AlertService,
        private messageService: MessageService,
        private router: Router,
        private route: ActivatedRoute,
        private taskService: TaskService,
    ) {}

    ngOnInit(): void {
        const user = this.authService.currentUserValue;
        this.currentUserId = user?.userId || null;
        this.route.queryParams.subscribe(params => {
            if (params['agentId']) {
                this.selectedAgentId = +params['agentId'];
                this.agentFilter = 'specific';
            }
        });
        this.loadAll();
        this.loadSalesAgents();
        this.ownerLeadService.getMyTeamUserIds().subscribe({
            next: (res: any) => {
                this.teamUserIds = res?.data?.teamUserIds || [];
            },
            error: () => { this.teamUserIds = []; }
        });
    }

    // Close all overlays when clicking outside
    @HostListener('document:click')
    onDocumentClick(): void {
        this.statusMenuOpenId = null;
        this.agentMenuOpenId = null;
        this.bulkStatusMenuOpen = false;
        this.bulkAgentMenuOpen = false;
    }

    // ─── Data loading ─────────────────────────────────────────

    loadAll(): void {
        this.metricsLoading = true;
        this.tableLoading = true;
        this.activityLoading = true;

        const agentUserId = this.agentFilter === 'me' && this.currentUserId
            ? this.currentUserId
            : undefined;

        const metrics$ = this.ownerLeadService.getPipelineMetrics(this.metricsWindowDays).pipe(
            catchError(() => of(null))
        );
        const funnel$ = this.ownerLeadService.getFunnelCounts().pipe(
            catchError(() => of([]))
        );
        const leads$ = this.ownerLeadService.findAllFiltered(true, agentUserId).pipe(
            map((res: any) => res?.data?.ownerLeadProfiles || res?.data || []),
            catchError(() => of([]))
        );

        this.loadVelocity();

        forkJoin({ metrics: metrics$, funnel: funnel$, leads: leads$ }).subscribe({
            next: ({ metrics, funnel, leads }) => {
                this.applyMetrics(metrics);
                this.applyFunnelCounts(funnel);
                this.buildRows(leads as any[]);
                this.lastRefresh = new Date();
                this.metricsLoading = false;
                this.tableLoading = false;

                // Batch fetch view stats for all proformas in the lead list
                const proformaIds = (leads as any[])
                    .filter((l: any) => l.proformaId)
                    .map((l: any) => l.proformaId as number);
                if (proformaIds.length) {
                    this.proformaService.getViewStatsBatch(proformaIds).subscribe({
                        next: (res: any) => {
                            this.viewStatsMap = res?.data?.viewStats || {};
                            this.applyViewStatsToRows();
                            this.applyFilters();
                        },
                        error: () => { /* view stats are non-critical */ }
                    });
                }

                this.loadActivityFeed();
            },
            error: (err) => {
                this.alertService.error(err);
                this.metricsLoading = false;
                this.tableLoading = false;
                this.activityLoading = false;
            },
        });
    }

    private loadSalesAgents(): void {
        this.userService.getSalesmen().subscribe({
            next: (res: any) => {
                this.salesAgents = res?.data?.users || res?.data || [];
                if (this.agentFilter === 'specific' && this.selectedAgentId != null) {
                    const match = this.salesAgents.find(a => a.userId === this.selectedAgentId);
                    this.selectedAgentName = match ? this.formatAgent(match) : String(this.selectedAgentId);
                }
            },
            error: () => { this.salesAgents = []; }
        });
    }

    loadVelocity(): void {
        this.velocityLoading = true;
        this.ownerLeadService.getVelocityMetrics(90).subscribe({
            next: (data: any) => {
                this.velocityStages = data?.stages || [];
                this.velocityLoading = false;
            },
            error: () => { this.velocityLoading = false; }
        });
    }

    // ─── Data processing ──────────────────────────────────────

    private applyMetrics(res: any): void {
        if (!res?.data?.metrics) return;
        const m = res.data.metrics;
        this.totalActiveLeads = m.activeLeads ?? 0;
        this.proformasSentMonth = m.proformasSentInWindow ?? 0;
        this.leadsWithViewsThisWeek = m.hotLeadsInWindow ?? 0;
        this.convertedThisMonth = m.convertedInWindow ?? 0;
        this.metricsWindowDays = m.windowDays ?? 30;
    }

    private applyFunnelCounts(res: any): void {
        const funnel: any[] = Array.isArray(res) ? res : (res?.data ?? []);
        if (!Array.isArray(funnel) || funnel.length === 0) return;
        const countMap: { [status: number]: number } = {};
        let total = 0;
        funnel.forEach((f: any) => {
            const s = Number(f.leadStatus);
            countMap[s] = Number(f.count) || 0;
            total += countMap[s];
        });
        this.funnelStages = this.funnelStages.map(stage => ({
            ...stage,
            count: countMap[stage.value] || 0,
        }));
        this.funnelTotal = total;
    }

    private buildRows(leads: any[]): void {
        const now = new Date();
        const staleDays = 30;

        this.rows = leads.map(l => {
            const rawProps: any[] = l.propertyLeadProfiles || [];
            const propertyName = rawProps.length > 0
                ? (rawProps[0].propertyCode || rawProps[0].subjAdrDevelopmentName || rawProps[0].subjAdrStreet || '—')
                : (l.propertyCode || l.subjAdrDevelopmentName || '—');

            const create = l.createDate ? new Date(l.createDate) : null;
            const lastUpdate = l.lastUpdate ? new Date(l.lastUpdate) : null;
            const daysActive = create
                ? Math.floor((now.getTime() - create.getTime()) / (1000 * 60 * 60 * 24))
                : 0;
            const daysSinceUpdate = lastUpdate
                ? Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24))
                : daysActive;
            const isStale = daysSinceUpdate > staleDays && ![4, 5, 7].includes(Number(l.leadStatus));
            const sentDate = l.proformaSentDate ? new Date(l.proformaSentDate) : null;
            const leadScore = this.computeLeadScore(l, daysSinceUpdate);

            const properties: PropertySubRow[] = rawProps.map((p: any, idx: number) => {
                const propProformaId = p.proformaId || (idx === 0 ? (l.proformaId || null) : null);
                const propProformaConfId = p.proformaConfId || (idx === 0 ? (l.proformaConfId || null) : null);
                const propSentDate = p.proformaSentDate
                    ? new Date(p.proformaSentDate)
                    : (idx === 0 && sentDate ? sentDate : null);

                const displayName = p.propertyCode
                    || (p.subjAdrDevelopmentName
                        ? (p.subjAdrDevelopmentName + (p.subjAdrUnit ? ' #' + p.subjAdrUnit : ''))
                        : (p.subjAdrStreet || `Property #${p.propertyLeadProfileId}`));

                return {
                    propertyLeadProfileId: p.propertyLeadProfileId,
                    displayName,
                    developmentName: p.subjAdrDevelopmentName || '—',
                    bedroomNum: p.bedroomNum ?? null,
                    bathroomNum: p.bathroomNum ?? null,
                    subjValue: p.subjValue ?? null,
                    proformaId: propProformaId,
                    proformaConfId: propProformaConfId,
                    proformaSent: propSentDate,
                    viewCount: 0,
                    lastViewed: null,
                    hot: false,
                };
            });

            const assignedAgentObj = l.assignToSalesAgnt1;
            return {
                ownerLeadProfileId: l.ownerLeadProfileId,
                ownerName: this.formatName(l),
                propertyName,
                leadStatus: Number(l.leadStatus) || 0,
                leadStatusLabel: this.getLeadStatusLabel(Number(l.leadStatus)),
                assignedAgent: this.formatAgent(assignedAgentObj),
                assignedAgentId: assignedAgentObj?.userId || null,
                proformaConfId: l.proformaConfId || null,
                proformaId: l.proformaId || null,
                proformaSent: sentDate,
                lastViewed: null,
                viewCount: 0,
                daysActive,
                daysSinceUpdate,
                isStale,
                leadScore,
                hot: false,
                createDate: create,
                lastUpdate,
                properties,
                rawData: l,
            };
        });

        this.applyFilters();
    }

    private computeLeadScore(l: any, daysSinceUpdate: number): number {
        const statusScores: { [s: number]: number } = { 1: 5, 2: 20, 3: 50, 4: 100, 5: 0, 6: 10, 7: 0 };
        const statusScore = statusScores[Number(l.leadStatus)] || 0;

        // Recency bonus
        const recency = daysSinceUpdate <= 7 ? 20 : daysSinceUpdate <= 30 ? 10 : 0;

        // Proforma sent bonus
        const proformaBonus = l.proformaId ? (l.proformaSentDate ? 15 : 5) : 0;

        // Properties bonus
        const propBonus = Math.min((l.propertyLeadProfiles?.length || 0) * 5, 10);

        return Math.min(statusScore + recency + proformaBonus + propBonus, 100);
    }

    private applyViewStatsToRows(): void {
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        let weekCount = 0;
        this.rows.forEach(row => {
            if (row.proformaId) {
                const stats = this.viewStatsMap[String(row.proformaId)];
                if (stats) {
                    row.viewCount = stats.viewCount || 0;
                    row.lastViewed = stats.lastViewedAt ? new Date(stats.lastViewedAt) : null;
                    row.hot = row.viewCount >= 3;
                    if (row.lastViewed && row.lastViewed >= sevenDaysAgo) weekCount++;
                    // Boost lead score with view data
                    row.leadScore = Math.min(row.leadScore + Math.min(row.viewCount * 5, 25), 100);
                }
            }
            row.properties.forEach(prop => {
                if (!prop.proformaId) return;
                const stats = this.viewStatsMap[String(prop.proformaId)];
                if (!stats) return;
                prop.viewCount = stats.viewCount || 0;
                prop.lastViewed = stats.lastViewedAt ? new Date(stats.lastViewedAt) : null;
                prop.hot = prop.viewCount >= 3;
            });
        });
        if (!this.leadsWithViewsThisWeek) {
            this.leadsWithViewsThisWeek = weekCount;
        }
    }

    private loadActivityFeed(): void {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        const dateFrom = thirtyDaysAgo.toISOString().split('T')[0];

        const fetchEvents = (eventType: string) =>
            this.appEventService.findByFilter(
                0, 30, 'clickedAt', -1, null, null, null,
                { eventType, dateFrom }
            ).pipe(
                map((res: any) => (res?.data?.appEvents || res?.data?.content || []) as any[]),
                catchError(() => of([] as any[]))
            );

        const eventTypes = ['PROFORMA_SENT', 'LEAD_STATUS_CHANGE', 'LEAD_NOTE', 'LEAD_CALL', 'LEAD_EMAIL', 'LEAD_MEETING'];

        forkJoin(
            eventTypes.reduce((acc: any, type) => { acc[type] = fetchEvents(type); return acc; }, {})
        ).subscribe({
            next: (results: any) => {
                const items: ActivityItem[] = [];
                const iconMap: { [t: string]: string } = {
                    PROFORMA_SENT: 'pi pi-send',
                    LEAD_STATUS_CHANGE: 'pi pi-tag',
                    LEAD_NOTE: 'pi pi-file',
                    LEAD_CALL: 'pi pi-phone',
                    LEAD_EMAIL: 'pi pi-envelope',
                    LEAD_MEETING: 'pi pi-calendar',
                };
                const labelMap: { [t: string]: string } = {
                    PROFORMA_SENT: 'Proforma sent',
                    LEAD_STATUS_CHANGE: 'Status changed',
                    LEAD_NOTE: 'Note logged',
                    LEAD_CALL: 'Call logged',
                    LEAD_EMAIL: 'Email logged',
                    LEAD_MEETING: 'Meeting logged',
                };

                eventTypes.forEach(type => {
                    (results[type] || []).forEach((e: any) => {
                        let text = labelMap[type] || type;
                        if (e.metadata) {
                            try {
                                const meta = JSON.parse(e.metadata);
                                if (meta.ownerName) text += ` — ${meta.ownerName}`;
                                else if (meta.note) text += ` — ${meta.note.substring(0, 60)}${meta.note.length > 60 ? '…' : ''}`;
                                else text += ` — ${e.metadata}`;
                            } catch {
                                text += ` — ${e.metadata}`;
                            }
                        }
                        items.push({ text, date: new Date(e.clickedAt), icon: iconMap[type] || 'pi pi-circle', type });
                    });
                });

                items.sort((a, b) => b.date.getTime() - a.date.getTime());
                this.activityFeed = items.slice(0, 20);
                this.activityLoading = false;
            },
            error: () => {
                this.activityFeed = [];
                this.activityLoading = false;
            }
        });
    }

    // ─── Filters ──────────────────────────────────────────────

    setDateRange(value: 'all' | 'week' | 'month' | 'quarter'): void {
        this.dateRangeFilter = value;
        this.applyFilters();
    }

    private getDateRangeStart(): Date | null {
        const now = new Date();
        if (this.dateRangeFilter === 'week') {
            const d = new Date(now);
            d.setDate(d.getDate() - d.getDay());
            d.setHours(0, 0, 0, 0);
            return d;
        }
        if (this.dateRangeFilter === 'month') {
            return new Date(now.getFullYear(), now.getMonth(), 1);
        }
        if (this.dateRangeFilter === 'quarter') {
            const q = Math.floor(now.getMonth() / 3);
            return new Date(now.getFullYear(), q * 3, 1);
        }
        return null;
    }

    setAgentFilter(filter: 'all' | 'me' | 'team' | 'specific'): void {
        if (this.agentFilter === filter) return;
        this.agentFilter = filter;
        if (filter === 'team' || filter === 'specific') {
            this.applyFilters();
        } else {
            this.loadAll();
        }
    }

    clearSpecificAgentFilter(): void {
        this.selectedAgentId = null;
        this.selectedAgentName = null;
        this.agentFilter = 'all';
        this.loadAll();
    }

    selectStage(stage: number | null): void {
        this.selectedStage = (this.selectedStage === stage) ? null : stage;
        this.applyFilters();
    }

    onSearchChange(): void {
        this.applyFilters();
    }

    clearSearch(): void {
        this.searchQuery = '';
        this.applyFilters();
    }

    private applyFilters(): void {
        let result = [...this.rows];

        if (this.selectedStage != null) {
            result = result.filter(r => r.leadStatus === this.selectedStage);
        }

        const q = this.searchQuery.trim().toLowerCase();
        if (q) {
            result = result.filter(r =>
                r.ownerName.toLowerCase().includes(q) ||
                r.propertyName.toLowerCase().includes(q) ||
                r.assignedAgent.toLowerCase().includes(q) ||
                r.leadStatusLabel.toLowerCase().includes(q)
            );
        }

        const rangeStart = this.getDateRangeStart();
        if (rangeStart) {
            result = result.filter(r => r.createDate && r.createDate >= rangeStart);
        }

        if (this.agentFilter === 'team') {
            if (this.teamUserIds && this.teamUserIds.length > 0) {
                result = result.filter(r =>
                    this.teamUserIds!.includes(r.rawData?.assignToSalesAgnt1UserId) ||
                    this.teamUserIds!.includes(r.rawData?.assignToSalesAgnt2UserId) ||
                    this.teamUserIds!.includes(r.rawData?.assignToSalesAgnt3UserId)
                );
            }
            // If no team configured, show all (don't filter)
        }

        if (this.agentFilter === 'specific' && this.selectedAgentId != null) {
            result = result.filter(r => r.assignedAgentId === this.selectedAgentId);
        }

        this.filteredRows = result;
    }

    // ─── Expand rows ──────────────────────────────────────────

    toggleRow(row: PipelineRow): void {
        const key = String(row.ownerLeadProfileId);
        if (this.expandedRowKeys[key]) {
            delete this.expandedRowKeys[key];
            if (this.noteOpenId === row.ownerLeadProfileId) {
                this.noteOpenId = null;
                this.noteText = '';
            }
        } else {
            this.expandedRowKeys[key] = true;
            this.loadLeadMessages(row.ownerLeadProfileId);
        }
        this.expandedRowKeys = { ...this.expandedRowKeys };
    }

    isRowExpanded(row: PipelineRow): boolean {
        return !!this.expandedRowKeys[String(row.ownerLeadProfileId)];
    }

    /**
     * Lazy-load the message thread for a single lead. Read-only view of
     * email + SMS sent via the bizdev compose dialog.
     */
    loadLeadMessages(leadId: number): void {
        if (leadId == null) return;
        if (this.leadMessages[leadId] != null) return; // already loaded
        this.leadMessagesLoading[leadId] = true;
        this.ownerLeadService.getLeadMessages(leadId).subscribe({
            next: (res: any) => {
                const list = res?.data?.messages || res?.messages || [];
                this.leadMessages[leadId] = list;
                this.leadMessagesLoading[leadId] = false;
            },
            error: () => {
                this.leadMessages[leadId] = [];
                this.leadMessagesLoading[leadId] = false;
            }
        });
    }

    // ─── Inline status change ─────────────────────────────────

    openStatusMenu(event: MouseEvent, row: PipelineRow): void {
        event.stopPropagation();
        this.agentMenuOpenId = null;
        this.statusMenuOpenId = this.statusMenuOpenId === row.ownerLeadProfileId
            ? null
            : row.ownerLeadProfileId;
    }

    selectStatus(event: MouseEvent, row: PipelineRow, status: { name: string; value: number }): void {
        event.stopPropagation();
        if (status.value === row.leadStatus) {
            this.statusMenuOpenId = null;
            return;
        }
        this.statusMenuOpenId = null;
        this.statusUpdating[row.ownerLeadProfileId] = true;

        const payload = { ...row.rawData, leadStatus: status.value };
        this.ownerLeadService.update(payload).subscribe({
            next: () => {
                row.leadStatus = status.value;
                row.leadStatusLabel = status.name;
                row.rawData = { ...row.rawData, leadStatus: status.value };
                row.isStale = false;
                delete this.statusUpdating[row.ownerLeadProfileId];
                this.applyFilters();
                this.messageService.add({
                    severity: 'success',
                    summary: 'Status updated',
                    detail: `${row.ownerName} → ${status.name}`,
                    life: 3000,
                });
            },
            error: (err) => {
                delete this.statusUpdating[row.ownerLeadProfileId];
                this.messageService.add({
                    severity: 'error',
                    summary: 'Update failed',
                    detail: 'Could not update lead status.',
                    life: 4000,
                });
            }
        });
    }

    // ─── Inline agent assign ──────────────────────────────────

    openAgentMenu(event: MouseEvent, row: PipelineRow): void {
        event.stopPropagation();
        this.statusMenuOpenId = null;
        this.agentMenuOpenId = this.agentMenuOpenId === row.ownerLeadProfileId
            ? null
            : row.ownerLeadProfileId;
    }

    selectAgent(event: MouseEvent, row: PipelineRow, agent: any): void {
        event.stopPropagation();
        this.agentMenuOpenId = null;
        if (agent.userId === row.assignedAgentId) return;

        this.agentUpdating[row.ownerLeadProfileId] = true;
        const payload = {
            ...row.rawData,
            assignToSalesAgnt1: { userId: agent.userId },
        };
        this.ownerLeadService.update(payload).subscribe({
            next: () => {
                row.assignedAgent = this.formatAgent(agent);
                row.assignedAgentId = agent.userId;
                row.rawData = { ...row.rawData, assignToSalesAgnt1: agent };
                delete this.agentUpdating[row.ownerLeadProfileId];
                this.messageService.add({
                    severity: 'success',
                    summary: 'Agent assigned',
                    detail: `${row.ownerName} → ${this.formatAgent(agent)}`,
                    life: 3000,
                });
            },
            error: () => {
                delete this.agentUpdating[row.ownerLeadProfileId];
                this.messageService.add({
                    severity: 'error',
                    summary: 'Update failed',
                    detail: 'Could not assign agent.',
                    life: 4000,
                });
            }
        });
    }

    unassignAgent(event: MouseEvent, row: PipelineRow): void {
        event.stopPropagation();
        this.agentMenuOpenId = null;
        if (!row.assignedAgentId) return;

        this.agentUpdating[row.ownerLeadProfileId] = true;
        const payload = { ...row.rawData, assignToSalesAgnt1: null };
        this.ownerLeadService.update(payload).subscribe({
            next: () => {
                row.assignedAgent = '—';
                row.assignedAgentId = null;
                row.rawData = { ...row.rawData, assignToSalesAgnt1: null };
                delete this.agentUpdating[row.ownerLeadProfileId];
            },
            error: () => {
                delete this.agentUpdating[row.ownerLeadProfileId];
            }
        });
    }

    // ─── Bulk selection ───────────────────────────────────────

    toggleRowSelection(event: MouseEvent, row: PipelineRow): void {
        event.stopPropagation();
        const idx = this.selectedRows.findIndex(r => r.ownerLeadProfileId === row.ownerLeadProfileId);
        if (idx >= 0) {
            this.selectedRows.splice(idx, 1);
        } else {
            this.selectedRows.push(row);
        }
        this.selectedRows = [...this.selectedRows];
    }

    isRowSelected(row: PipelineRow): boolean {
        return this.selectedRows.some(r => r.ownerLeadProfileId === row.ownerLeadProfileId);
    }

    toggleSelectAll(): void {
        if (this.selectedRows.length === this.filteredRows.length) {
            this.selectedRows = [];
        } else {
            this.selectedRows = [...this.filteredRows];
        }
    }

    clearSelection(): void {
        this.selectedRows = [];
        this.bulkStatusMenuOpen = false;
        this.bulkAgentMenuOpen = false;
    }

    applyBulkStatus(status: { name: string; value: number }): void {
        if (!this.selectedRows.length) return;
        this.bulkStatusMenuOpen = false;
        this.bulkUpdating = true;
        const ids = this.selectedRows.map(r => r.ownerLeadProfileId);
        this.ownerLeadService.bulkUpdate({ ids, updateField: 'STATUS', leadStatus: status.value }).subscribe({
            next: (res: any) => {
                this.bulkUpdating = false;
                const updatedCount = res?.data?.updatedCount || ids.length;
                // Optimistic update
                this.selectedRows.forEach(row => {
                    row.leadStatus = status.value;
                    row.leadStatusLabel = status.name;
                    row.rawData = { ...row.rawData, leadStatus: status.value };
                });
                this.selectedRows = [];
                this.applyFilters();
                this.messageService.add({
                    severity: 'success',
                    summary: 'Bulk update complete',
                    detail: `${updatedCount} lead(s) updated to ${status.name}`,
                    life: 3000,
                });
            },
            error: () => {
                this.bulkUpdating = false;
                this.messageService.add({ severity: 'error', summary: 'Bulk update failed', detail: 'Could not update leads.', life: 4000 });
            }
        });
    }

    applyBulkAgent(agent: any): void {
        if (!this.selectedRows.length) return;
        this.bulkAgentMenuOpen = false;
        this.bulkUpdating = true;
        const ids = this.selectedRows.map(r => r.ownerLeadProfileId);
        this.ownerLeadService.bulkUpdate({ ids, updateField: 'AGENT', agentUserId: agent.userId }).subscribe({
            next: (res: any) => {
                this.bulkUpdating = false;
                const updatedCount = res?.data?.updatedCount || ids.length;
                const agentName = this.formatAgent(agent);
                this.selectedRows.forEach(row => {
                    row.assignedAgent = agentName;
                    row.assignedAgentId = agent.userId;
                    row.rawData = { ...row.rawData, assignToSalesAgnt1: agent };
                });
                this.selectedRows = [];
                this.messageService.add({
                    severity: 'success',
                    summary: 'Agent assigned',
                    detail: `${updatedCount} lead(s) assigned to ${agentName}`,
                    life: 3000,
                });
            },
            error: () => {
                this.bulkUpdating = false;
                this.messageService.add({ severity: 'error', summary: 'Bulk assign failed', detail: 'Could not assign agent.', life: 4000 });
            }
        });
    }

    // ─── Log activity (note/call/email/meeting) ────────────────

    openNotePanel(event: MouseEvent, row: PipelineRow): void {
        event.stopPropagation();
        if (this.noteOpenId === row.ownerLeadProfileId) {
            this.noteOpenId = null;
            this.noteText = '';
        } else {
            // Expand the row first if not already expanded
            if (!this.isRowExpanded(row)) {
                this.expandedRowKeys[String(row.ownerLeadProfileId)] = true;
                this.expandedRowKeys = { ...this.expandedRowKeys };
            }
            this.noteOpenId = row.ownerLeadProfileId;
            this.noteText = '';
            this.noteType = 'NOTE';
        }
    }

    closeNotePanel(): void {
        this.noteOpenId = null;
        this.noteText = '';
    }

    saveNote(row: PipelineRow): void {
        const text = this.noteText.trim();
        if (!text) return;
        this.noteSaving = true;

        const eventType = `LEAD_${this.noteType}`;
        const meta = {
            note: text,
            noteType: this.noteType,
            leadId: row.ownerLeadProfileId,
            ownerName: row.ownerName,
        };

        this.appEventService.logActivity(eventType, meta).subscribe({
            next: () => {
                this.noteSaving = false;
                this.noteText = '';
                this.noteOpenId = null;

                const iconMap: { [t: string]: string } = {
                    NOTE: 'pi pi-file', CALL: 'pi pi-phone',
                    EMAIL: 'pi pi-envelope', MEETING: 'pi pi-calendar',
                };
                const label = this.noteTypes.find(n => n.value === this.noteType)?.name || 'Activity';
                this.activityFeed.unshift({
                    text: `${label} logged — ${row.ownerName}: ${text.substring(0, 60)}${text.length > 60 ? '…' : ''}`,
                    date: new Date(),
                    icon: iconMap[this.noteType] || 'pi pi-circle',
                    type: eventType,
                });
                if (this.activityFeed.length > 20) this.activityFeed.pop();

                this.messageService.add({
                    severity: 'success',
                    summary: `${label} logged`,
                    detail: `Activity recorded for ${row.ownerName}`,
                    life: 3000,
                });
            },
            error: () => {
                this.noteSaving = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Save failed',
                    detail: 'Could not log activity.',
                    life: 4000,
                });
            }
        });
    }

    // ─── Follow-up scheduling ─────────────────────────────────

    openFollowUp(row: any): void {
        this.followUpOpenId = this.followUpOpenId === row.ownerLeadProfileId ? null : row.ownerLeadProfileId;
        this.followUpDate = '';
        this.followUpNote = '';
    }

    saveFollowUp(row: any): void {
        if (!this.followUpDate) return;
        this.followUpSaving = true;
        const task: any = {
            name: 'Follow-up: ' + (row.ownerName || 'Lead'),
            status: 2,
            priority: 4,
            dateDue: this.followUpDate + 'T09:00:00',
            taskSource: 5,
            taskSourceId: row.ownerLeadProfileId,
            description: this.followUpNote || null,
        };
        this.taskService.add(task).subscribe({
            next: () => {
                this.followUpSaving = false;
                this.followUpOpenId = null;
                const dateLabel = new Date(this.followUpDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                this.activityFeed.unshift({
                    text: 'Follow-up scheduled for ' + (row.ownerName || 'lead') + ' on ' + dateLabel,
                    icon: 'pi pi-clock',
                    date: new Date(),
                    type: 'FOLLOW_UP',
                });
                if (this.activityFeed.length > 20) this.activityFeed.pop();
                this.messageService.add({
                    severity: 'success',
                    summary: 'Follow-up scheduled',
                    detail: `Task created for ${row.ownerName} on ${dateLabel}`,
                    life: 3000,
                });
            },
            error: () => {
                this.followUpSaving = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Save failed',
                    detail: 'Could not create follow-up task.',
                    life: 4000,
                });
            },
        });
    }

    // ─── Funnel helpers ───────────────────────────────────────

    funnelBarWidth(stage: FunnelStage): string {
        const max = Math.max(...this.funnelStages.map(s => s.count), 1);
        return Math.round((stage.count / max) * 100) + '%';
    }

    funnelConversion(idx: number): string {
        const from = this.funnelStages[idx]?.count || 0;
        const to = this.funnelStages[idx + 1]?.count || 0;
        if (from === 0) return '—';
        return Math.round((to / from) * 100) + '%';
    }

    // ─── Score helpers ────────────────────────────────────────

    scoreClass(score: number): string {
        if (score >= 70) return 'bp-score bp-score-high';
        if (score >= 40) return 'bp-score bp-score-mid';
        return 'bp-score bp-score-low';
    }

    // ─── Display helpers ──────────────────────────────────────

    private formatName(l: any): string {
        const parts = [l.firstName, l.lastName].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : (l.fullName || l.emailAddress || `Lead #${l.ownerLeadProfileId}`);
    }

    formatAgent(agent: any): string {
        if (!agent) return '—';
        const parts = [agent.firstName, agent.lastName].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : (agent.username || '—');
    }

    getLeadStatusLabel(status: number): string {
        const found = this.ownerLeadService.leadStatuses.find(s => s.value === status);
        return found ? found.name : '—';
    }

    statusBadgeClass(status: number): string {
        switch (status) {
            case 1: return 'bp-badge bp-badge-prospect';
            case 2: return 'bp-badge bp-badge-lead';
            case 3: return 'bp-badge bp-badge-opportunity';
            case 4: return 'bp-badge bp-badge-win';
            case 5: return 'bp-badge bp-badge-loss';
            case 6: return 'bp-badge bp-badge-contact';
            default: return 'bp-badge';
        }
    }

    engagementBadgeClass(prop: PropertySubRow): string {
        if (prop.viewCount >= 3) return 'bp-eng-badge bp-eng-hot';
        if (prop.viewCount >= 1) return 'bp-eng-badge bp-eng-viewed';
        return 'bp-eng-badge bp-eng-none';
    }

    engagementBadgeLabel(prop: PropertySubRow): string {
        if (prop.viewCount >= 3) return prop.viewCount + '× Hot';
        if (prop.viewCount >= 1) return prop.viewCount + '× Viewed';
        return 'Not viewed';
    }

    proformaStatusLabel(prop: PropertySubRow): string {
        if (!prop.proformaId) return 'Not Created';
        if (prop.proformaSent) {
            const d = prop.proformaSent;
            return 'Sent ' + (d.getMonth() + 1) + '/' + d.getDate();
        }
        return 'Draft';
    }

    // ─── Export CSV ───────────────────────────────────────────

    exportCsv(): void {
        const columns: Array<{ header: string; get: (r: PipelineRow) => string }> = [
            { header: 'Owner',           get: (r) => r.ownerName },
            { header: 'Property',        get: (r) => r.propertyName },
            { header: 'Status',          get: (r) => r.leadStatusLabel },
            { header: 'Agent',           get: (r) => r.assignedAgent },
            { header: 'Score',           get: (r) => String(r.leadScore) },
            { header: 'Proforma Sent',   get: (r) => r.proformaSent ? (r.proformaSent as Date).toISOString().split('T')[0] : '' },
            { header: 'Last Viewed',     get: (r) => r.lastViewed ? (r.lastViewed as Date).toISOString().split('T')[0] : '' },
            { header: 'View Count',      get: (r) => String(r.viewCount) },
            { header: 'Days Active',     get: (r) => String(r.daysActive) },
            { header: 'Days Since Update', get: (r) => String(r.daysSinceUpdate) },
            { header: 'Stale',           get: (r) => r.isStale ? 'Yes' : 'No' },
        ];

        const csvEscape = (val: string): string => {
            if (val.includes(',') || val.includes('"') || val.includes('\n')) {
                return '"' + val.replace(/"/g, '""') + '"';
            }
            return val;
        };

        const header = columns.map(c => csvEscape(c.header)).join(',');
        const rows = this.filteredRows.map(r =>
            columns.map(c => csvEscape(c.get(r))).join(',')
        );
        const csv = [header, ...rows].join('\r\n');

        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bizdev-pipeline-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    // ─── Navigation ───────────────────────────────────────────

    viewProfile(row: PipelineRow): void {
        this.router.navigate(['/owner-lead-profile-view', row.ownerLeadProfileId]);
    }

    openProforma(row: PipelineRow): void {
        if (row.proformaId) {
            this.router.navigate(['/proforma-list', row.proformaId]);
        } else {
            this.router.navigate(['/proforma-list']);
            this.messageService.add({
                severity: 'info',
                summary: 'No proforma yet',
                detail: 'Create a proforma for this lead from the Proforma list.',
            });
        }
    }

    openPropertyProforma(prop: PropertySubRow): void {
        if (prop.proformaId) {
            this.router.navigate(['/proforma-list', prop.proformaId]);
        } else {
            this.router.navigate(['/proforma-list']);
            this.messageService.add({
                severity: 'info',
                summary: 'No proforma yet',
                detail: 'Create a proforma for this property from the Proforma list.',
            });
        }
    }

    openPropertyLead(prop: PropertySubRow): void {
        this.router.navigate(['/property-lead-profile', prop.propertyLeadProfileId]);
    }

    linkProperty(row: PipelineRow): void {
        this.router.navigate(['/property-lead-profile-list'], {
            queryParams: { ownerLeadProfileId: row.ownerLeadProfileId }
        });
    }

    createProformaForLead(row: PipelineRow, prop?: PropertySubRow): void {
        const params: any = { ownerLeadProfileId: row.ownerLeadProfileId };
        if (prop?.propertyLeadProfileId) params.propertyLeadProfileId = prop.propertyLeadProfileId;
        this.router.navigate(['/proforma-list'], { queryParams: params });
    }

    sendProforma(row: PipelineRow): void {
        this.openProforma(row);
    }

    // ─── Compose dialog ───────────────────────────────────────

    openCompose(row: any): void {
        this.composeRow = row;
        this.composeVisible = true;
    }

    onComposeSent(event: { channel: string; subject: string }): void {
        if (!this.composeRow) return;
        const label = event.channel === 'EMAIL'
            ? 'Email sent to ' + (this.composeRow.fullName || this.composeRow.ownerLeadName || 'lead')
            : 'SMS sent to ' + (this.composeRow.fullName || this.composeRow.ownerLeadName || 'lead');
        this.activityFeed.unshift({
            text: label,
            icon: event.channel === 'EMAIL' ? 'pi pi-envelope' : 'pi pi-mobile',
            date: new Date(),
            type: event.channel === 'EMAIL' ? 'LEAD_EMAIL' : 'LEAD_SMS',
        });
    }
}
